import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { client } from '@Utils'
import { simulationEndpoints } from '@URLS'
import { buildSavePayload } from '../useSaveProgress'
import { ResultsSummary } from './ResultsSummary'

export const SubmitBar = ({ activityPk, coursenum, state, chainState, onSubmitted, completedResults, canRetry: canRetryProp }) => {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState(completedResults || null)
  const [error, setError] = useState(null)
  const [canRetry, setCanRetry] = useState(canRetryProp || false)
  const [confirming, setConfirming] = useState(false)

  const doSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const payload = buildSavePayload(state, chainState)
      const res = await client.post(
        simulationEndpoints.submit({ coursenum, activityPk }),
        payload,
      )
      setSubmitted(true)
      // Build results object from submit response
      if (res.data.taskResults) {
        setResults({
          showPoints: res.data.points != null,
          taskResults: res.data.taskResults,
          totalEarned: res.data.points,
          totalPossible: res.data.pointsPossible,
          simulationGroups: res.data.simulationGroups || [],
        })
      }
      if (res.data.canRetry) setCanRetry(true)
      if (onSubmitted) onSubmitted(res.data)
    } catch (err) {
      setError('Submission failed. Please try again.')
      console.error('Submit failed:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleTryAgain = async () => {
    try {
      await client.post(simulationEndpoints.newAttempt({ coursenum, activityPk }))
      window.location.reload()
    } catch (err) {
      setError('Could not start a new attempt. Please try again.')
    }
  }

  // Whether every objective is complete.  Used to decide whether to warn the
  // student before submitting — NOT to gate whether they can submit at all.
  const allObjectivesComplete = chainState
    ? !!chainState.chainComplete
    : !!(state && state.allComplete)

  // Chains still wait until the chain reaches its end before offering submit —
  // the always-available Continue/Finish control drives chainComplete.
  // Standalone simulations are always submittable, even with unmet objectives.
  if (chainState && !chainState.chainComplete && !submitted) return null

  // Clicking Submit with unmet objectives asks for a one-step confirmation.
  const handleSubmit = () => {
    if (!allObjectivesComplete && !confirming) {
      setConfirming(true)
      return
    }
    doSubmit()
  }

  // Show completed results when revisiting a completed attempt
  if (completedResults && !submitted) {
    return (
      <div className="simulation-submit-bar">
        <ResultsSummary results={completedResults} />
        {canRetry && (
          <button onClick={handleTryAgain} className="btn btn-myed btn-primary mt-3">
            Try Again
          </button>
        )}
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="simulation-submit-bar">
        <div className="alert alert-success mb-0" style={{ marginBottom: results ? '0' : undefined }}>
          Submitted successfully!
        </div>
        {results && <ResultsSummary results={results} />}
        {canRetry && (
          <button onClick={handleTryAgain} className="btn btn-myed btn-primary mt-3">
            Try Again
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="simulation-submit-bar">
      {error && <div className="alert alert-danger">{error}</div>}
      {!allObjectivesComplete && (
        <div className="alert alert-warning">
          You haven&apos;t met every objective yet. You can still submit — you&apos;ll be graded on
          what you&apos;ve completed.
        </div>
      )}
      {confirming && !allObjectivesComplete ? (
        <div className="simulation-submit-confirm">
          <button
            onClick={doSubmit}
            disabled={submitting}
            className="btn btn-myed btn-primary"
          >
            {submitting ? 'Submitting...' : 'Submit anyway'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={submitting}
            className="btn btn-myed btn-outline-secondary ms-2"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="btn btn-myed btn-primary"
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      )}
    </div>
  )
}

SubmitBar.propTypes = {
  activityPk: PropTypes.string.isRequired,
  coursenum: PropTypes.string.isRequired,
  state: PropTypes.object,
  chainState: PropTypes.object,
  onSubmitted: PropTypes.func,
  completedResults: PropTypes.object,
  canRetry: PropTypes.bool,
}
